import dayjs from 'dayjs';
import type { Profile, ProfileUpdateRq } from '../user.types';
import { TenantSimpleModel } from '@repositories/tenantRepository/models/TenantSimpleModel';
import { UserGroupBaseModel } from '@repositories/userGroupsRepository/models/UserGroupBaseModel';

export class ProfileModel {
  private readonly _dto: Profile;
  private readonly _lastLoginedAt: DateTimeString;
  private readonly _lastPasswordChangeDate: DateTimeString;
  private readonly _roles: string[];
  private readonly _company: TenantSimpleModel[];
  private readonly _userGroups: UserGroupBaseModel[];

  constructor(dto?: Profile) {
    this._dto = dto ?? ({} as Profile);
    this._lastLoginedAt = dayjs(dto?.lastLoginedAt).format('YYYY-MM-DD');

    const lastPasswordChangeDate = dayjs(dto?.lastPasswordChangeDate).format('YYYY-MM-DD');
    this._lastPasswordChangeDate = lastPasswordChangeDate === 'Invalid Date' ? '-' : lastPasswordChangeDate;
    this._roles = dto?.roles?.split(',') || [];
    this._company = dto?.tenants?.map(tenant => new TenantSimpleModel(tenant)) || [];
    this._userGroups = dto?.userGroups?.map(usergroup => new UserGroupBaseModel(usergroup)) || [];
  }

  get originDTO(): Profile {
    return this._dto;
  }

  get address(): string {
    return this._dto.address || '';
  }

  get email(): string {
    return this._dto.email || '';
  }

  get id(): string {
    return this._dto.id || '';
  }

  get lastLoginedAt(): DateTimeString {
    return this._lastLoginedAt;
  }

  get lastPasswordChangeDate(): DateTimeString {
    return this._lastPasswordChangeDate;
  }

  get name(): string {
    return this._dto.name || '';
  }

  get phone(): string {
    return this._dto.phone || '';
  }

  get roles(): string[] {
    return this._roles;
  }

  get company(): TenantSimpleModel[] {
    return this._company;
  }

  get companyNames(): string {
    return this.company.map(({ name }) => name).join(', ');
  }

  get userGroups(): UserGroupBaseModel[] {
    return this._userGroups;
  }

  get userGroupNames(): string {
    return this.userGroups.map(({ name }) => name).join(', ');
  }

  getTenantUserGroups(tenantId: string): UserGroupBaseModel[] {
    return this.userGroups.filter(({ tenantId: tid }) => tid === tenantId);
  }

  get username(): string {
    return this._dto.username || '';
  }

  get userUpdateRq(): ProfileUpdateRq {
    return {
      name: this._dto.name || '',
      phone: this._dto.phone || '',
      address: this._dto.address || '',
    };
  }

  get mfaEnabled(): boolean {
    return this._dto.mfaEnabled || false;
  }

  get mfaSetupAt(): string | null {
    return this._dto.mfaSetupAt ? dayjs(this._dto.mfaSetupAt).format('YYYY.MM.DD, HH:mm') : null;
  }
}
